// src/store/slices/calendarSlice.ts
// 📅 Управление календарём, применениями, пропусками

import type { StateCreator } from 'zustand';
import { apiClient, dateTimeToDate, normalizeDates } from '../../services/api';
import { calculateCurrentStreak } from '../../data/achievementsData';
import { MAX_PAST_DAYS_TO_MARK, VALIDATION_MESSAGE } from '../../utils/constants';
import {
  syncApplicationsToCloud,
  getApplicationsFromCloud,
  mergeApplications,
  clearApplicationsFromCloud,
} from '../../utils/cloudStorage';

/** Вспомогательная функция для сравнения массивов дат */
function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA =[...a].sort();
  const sortedB =[...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

// ========== ТИПЫ ==========

export interface Application {
  date: string;
  marked: boolean;
  feedback?: 'good' | 'neutral' | 'discomfort';
  note?: string;
  createdAt: string;
}

export interface MissedDaysStats {
  consecutiveMisses: number;
  totalMisses: number;
  lastMissDate: string | null;
}

export interface Statistics {
  totalApplications: number;
  currentStreak: number;
  totalMissed: number;
  lastApplicationDate: string | null;
  completionRate: number;
}

// ========== ХЕЛПЕРЫ ==========

const daysBetween = (date1: string, date2: string): number => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// ========== SLICE STATE ==========

export interface CalendarSlice {
  // State
  plannedDates: string[];
  startDate: string | null;
  applications: Application[];
  missedDays: MissedDaysStats;
  currentStreak: number;

  // Actions
  initializeCalendar: (dates: string[], planStart?: string | null) => Promise<void>; // ✅ async для сверки дат
  markApplication: (
    date: string,
    applied: boolean,
    feedback?: Application['feedback'],
    note?: string
  ) => void;
  unmarkApplication: (date: string) => void;
  updateMissedDays: () => void;
  updateCurrentStreak: () => void;
  getStatistics: () => Statistics;
  resetCalendar: () => void;
  // Task 2: Cloud Storage методы
  syncApplicationsToCloud: () => Promise<void>;
  loadApplicationsFromCloud: () => Promise<void>;
}

// ========== INITIAL STATE ==========

const initialCalendarState = {
  plannedDates:[],
  startDate: null,
  applications:[],
  missedDays: {
    consecutiveMisses: 0,
    totalMisses: 0,
    lastMissDate: null,
  },
  currentStreak: 0,
};

// ========== SLICE CREATOR ==========

export const createCalendarSlice: StateCreator<
  CalendarSlice,
[],
[],
  CalendarSlice
> = (set, get) => ({
  ...initialCalendarState,

  initializeCalendar: async (dates: string[], planStart?: string | null) => {
    const normalizedDates = normalizeDates(dates);
    const normalizedPlanStart = planStart
      ? planStart.includes('T')
        ? dateTimeToDate(planStart)
        : planStart
      : null;
    const startDate = normalizedPlanStart || (normalizedDates.length > 0 ? normalizedDates[0] : null);
    const oldPlannedDates = get().plannedDates;

    // 🔍 КРИТИЧНО: Сверка дат с бекендом
    // Если даты изменились - анкета была сброшена, очищаем applications
    const datesChanged = !arraysEqual(oldPlannedDates, normalizedDates);

    if (datesChanged && oldPlannedDates.length > 0) {
      console.warn('⚠️ Даты календаря изменились! Анкета была сброшена. Очищаем applications.');

      // Очищаем applications локально
      set({ applications:[] });

      // Очищаем CloudStorage
      try {
        await clearApplicationsFromCloud();
      } catch (error) {
        console.error('❌ Ошибка очистки CloudStorage:', error);
      }

      // ⚠️ КРИТИЧНО БАГ FIX: НЕ вызываем checkAndUnlockAchievements здесь!
      // Причина: CloudStorage очищается асинхронно (clearApplicationsFromCloud может занять до 7 сек с retry)
      // Если вызвать checkAndUnlockAchievements сразу после clearApplicationsFromCloud,
      // он загрузит СТАРЫЕ данные из CloudStorage до завершения очистки
      // и покажет СТАРЫЕ тосты достижений пользователю!
      // Решение: достижения пересчитаются автоматически при следующем входе
      // или переходе на страницу достижений (AchievementsPage.tsx)
      // где используется правильная логика с resetTimestamp защитой
    }

    set({
      plannedDates: normalizedDates,
      startDate,
    });

    // Обновляем стрик после инициализации
    get().updateCurrentStreak();

    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Календарь инициализирован:', {
        dates: normalizedDates.length,
        startDate,
        datesChanged,
      });
    }
  },

  markApplication: (date, applied, feedback, note) => {
    if (!applied) return;

    // Валидация
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    const daysDiff = Math.floor(
      (today.getTime() - targetDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysDiff < 0) {
      throw new Error('Нельзя отметить будущий день');
    }

    if (daysDiff > MAX_PAST_DAYS_TO_MARK) {
      throw new Error(VALIDATION_MESSAGE);
    }

    // Task 1: Разрешаем отмечать дни вне плана (для "ранее применял")
    // if (!get().plannedDates.includes(date)) {
    //   throw new Error('День не запланирован');
    // }

    // Обновляем applications
    const { applications } = get();
    const existing = applications.find((a) => a.date === date);

    if (existing) {
      set({
        applications: applications.map((a) =>
          a.date === date
            ? {
                ...a,
                marked: true,
                feedback,
                note,
                createdAt: new Date().toISOString(),
              }
            : a
        ),
      });
    } else {
      set({
        applications:[
          ...applications,
          {
            date,
            marked: true,
            feedback,
            note,
            createdAt: new Date().toISOString(),
          },
        ],
      });
    }

    // Сбрасываем consecutiveMisses
    set({
      missedDays: {
        consecutiveMisses: 0,
        totalMisses: get().missedDays.totalMisses,
        lastMissDate: null,
      },
    });

    // Обновляем стрик
    get().updateCurrentStreak();

    // Отправка на бэкенд (без блокировки UI)
    apiClient.markApplication(date).catch((err: Error) => {
      console.error('❌ Ошибка отправки отметки на бекенд:', err.message);
      // ✅ FIX: Показываем ошибку пользователю через custom event
      window.dispatchEvent(new CustomEvent('sync-error', {
        detail: { type: 'backend', message: 'Не удалось синхронизировать с сервером' }
      }));
    });

    // Task 2: Синхронизация с Telegram Cloud Storage
    // ✅ FIX: Больше не проглатываем ошибки silently
    get().syncApplicationsToCloud().catch((err: Error) => {
      console.error('❌ Ошибка синхронизации с CloudStorage:', err.message);
      // ✅ FIX: Уведомляем пользователя
      window.dispatchEvent(new CustomEvent('sync-error', {
        detail: { type: 'cloudstorage', message: 'Не удалось сохранить в облако' }
      }));
    });
  },

  unmarkApplication: (date) => {
    const { applications } = get();

    set({
      applications: applications.map((a) =>
        a.date === date ? { ...a, marked: false } : a
      ),
    });

    get().updateCurrentStreak();
  },

  updateCurrentStreak: () => {
    const { plannedDates, applications } = get();
    const streak = calculateCurrentStreak(plannedDates, applications);
    set({ currentStreak: streak });
  },

  updateMissedDays: () => {
    const { plannedDates, applications } = get();

    const today = new Date().toISOString().split('T')[0];
    const missedDates = plannedDates.filter((date) => {
      const isPast = date < today;
      const isMarked = applications.some((a) => a.date === date && a.marked);
      return isPast && !isMarked;
    });

    let consecutiveMisses = 0;
    const sortedMissed =[...missedDates].sort().reverse();

    for (const missedDate of sortedMissed) {
      const daysDiff = daysBetween(missedDate, today);
      if (daysDiff <= consecutiveMisses * 3 + 7) {
        consecutiveMisses++;
      } else {
        break;
      }
    }

    set({
      missedDays: {
        consecutiveMisses,
        totalMisses: missedDates.length,
        lastMissDate: sortedMissed[0] || null,
      },
    });
  },

  getStatistics: () => {
    const { applications, plannedDates, currentStreak, missedDays } = get();

    const totalApplications = applications.filter((a) => a.marked).length;

    const lastApplication = applications
      .filter((a) => a.marked)
      .sort((a, b) => b.date.localeCompare(a.date))[0];

    const completionRate =
      plannedDates.length > 0
        ? Math.round((totalApplications / plannedDates.length) * 100)
        : 0;

    return {
      totalApplications,
      currentStreak,
      totalMissed: missedDays.totalMisses,
      lastApplicationDate: lastApplication?.date || null,
      completionRate,
    };
  },

  resetCalendar: () => {
    set(initialCalendarState);
  },

  // Task 2: Cloud Storage методы
  syncApplicationsToCloud: async () => {
    const { applications } = get();
    await syncApplicationsToCloud(applications);
  },

  loadApplicationsFromCloud: async () => {
    const cloudApps = await getApplicationsFromCloud();
    const localApps = get().applications;
    const merged = mergeApplications(localApps, cloudApps);
    set({ applications: merged });
    get().updateCurrentStreak();
    get().updateMissedDays();
  },
});

export { daysBetween };